import DownloadIcon from 'assets/icons/DownloadIcon'
import { useFinishProcess } from 'hooks/process/useFinishProcess/useFinishProcess'
import { useFetchAllProcesses } from 'hooks/process/useGetAllProcessCurrentCompany/useGetAllProcessCurrentCompany'
import useReportDownloaderByExcel from 'hooks/process/useGetExcelReportByProcess/use-get-excel-report-by-process'
import { useFetchProcesses } from 'hooks/process/useGetProcess/useGetProcess'
import { useNotification } from 'lib/context/notification.context'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { platformAdminRoutes } from 'router/routes'
import { getProcessDetail } from 'services/common/process/createProcess/create-process-service'
import {
  createZipReportJob,
  getZipReportJobStatus,
  ZipReportJobStatus,
} from 'services/common/reports/getProcessReport/get-process-reports-service'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import { ActionViewDownloadAddUsers } from 'views/components/UI/BluesiteTable/Actions/GridActions'
import { TextBoldBlue } from 'views/components/UI/BluesiteTable/Actions/styled'
import { StrokedButton } from 'views/components/UI/Buttons'
import Loader from 'views/components/UI/Loader'
import { SubtitleBold } from 'views/components/UI/Text'
import { MainLayout } from 'views/layouts'

import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import SelectSearch from '../../Analytics/selectSearch'
import { HEADERS_SENT_PROCESSES } from '../constants'
import { DownloadProcess, IDownloadProcess } from '../store'
import ModalDateFilter from './components/ModalDateFilter'
import {
  Container,
  DownloadWrapper,
  EmptyStateText,
  HeaderActions,
  HeaderWrapper,
  JobStatusCard,
  JobStatusHeader,
  JobStatusMeta,
  JobStatusTitle,
  LoaderWrapper,
  ProgressFill,
  ProgressTrack,
  TableContainer,
  ViewWrapper,
  Wrapper,
  WrapperEmptyState,
} from './styled'

const ZIP_REPORT_JOB_STORAGE_KEY = 'activeZipReportJobId'
const ZIP_REPORT_POLLING_INTERVAL_MS = 5000

const logZipJobError = (context: string, error: any) => {
  const responseData = error?.response?.data

  console.error(`[ZIP JOB] ${context}`, {
    message: error?.message,
    status: error?.response?.status,
    url: error?.config?.url,
    method: error?.config?.method,
    responseData,
    error,
  })
}

const triggerZipDownload = (url: string) => {
  const link = document.createElement('a')

  link.href = url
  link.download = ''
  link.rel = 'noopener noreferrer'
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const getUnansweredRatio = (unansweredUsers: number, totalUsers: number) => (
  <span>
    <span style={{ color: '#C6016B', fontWeight: 700 }}>{unansweredUsers}</span>
    /{totalUsers}
  </span>
)

const DeliveredProcesses = () => {
  const navigate = useNavigate()
  const { handleFinishProcess } = useFinishProcess()
  const { getError, getSuccess, getWarning } = useNotification()

  const { listProcess, fetchProcesses, loadingListProcesses, paginationListProcess } = useFetchProcesses()
  const { fetchProcesses: fetchAllProcess } = useFetchProcesses()
  const { allProcess, fetchAllProcesses } = useFetchAllProcesses()

  const { fetchGetReportsByExcel } = useReportDownloaderByExcel()
  const [loading, setLoading] = useState(false)
  const [processSelected, setProcessSelected] = useState<number | ''>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [processIds, setProcessIds] = useState<Set<number>>(new Set())
  const [isDisabled, setIsDisabled] = useState(false)
  const [showModalDateFilter, setShowModalDateFilter] = useState(false)
  const [zipJobStatus, setZipJobStatus] = useState<ZipReportJobStatus | null>(null)
  const [isCreatingZipJob, setIsCreatingZipJob] = useState(false)
  const completedZipJobRef = useRef<number | null>(null)
  const processedReports =
    (zipJobStatus?.completed_reports || 0) + (zipJobStatus?.failed_reports || 0)
  const totalReports = zipJobStatus?.total_reports || 0
  const isPackagingZip =
    zipJobStatus?.status === 'processing' && totalReports > 0 && processedReports >= totalReports

  useEffect(() => {
    if (processSelected === '') {
      fetchAllProcesses()
    }
  }, [processSelected, fetchAllProcesses])

  useEffect(() => {
    if (processSelected) {
      fetchProcesses({ sent: true, processId: processSelected, evaluatedOnly: false })
    } else {
      fetchProcesses({ sent: true, evaluatedOnly: false })
    }
  }, [allProcess, processSelected, fetchProcesses, fetchAllProcess])

  useEffect(() => {
    setIsDisabled(processIds.size > 0)
  }, [processIds])

  function handleViewProcess(id: number) {
    navigate(platformAdminRoutes.DetailProcessDelivered, { state: { id } })
  }

  const handleSelectProcess = (processId: number) => {
    setProcessSelected(processId)
    setCurrentPage(1)
  }

  const handleDateFilterButtonClick = () => {
    setShowModalDateFilter(true)
  }

  const clearZipJobTracking = useCallback(() => {
    localStorage.removeItem(ZIP_REPORT_JOB_STORAGE_KEY)
    setZipJobStatus(null)
    completedZipJobRef.current = null
  }, [])

  const handleZipJobUpdate = useCallback(
    (job: ZipReportJobStatus) => {
      setZipJobStatus(job)

      if (job.status === 'completed' && job.file_link && completedZipJobRef.current !== job.id) {
        completedZipJobRef.current = job.id
        triggerZipDownload(job.file_link)
        getSuccess('El ZIP de reportes por usuario ya esta listo y comenzo la descarga.')
      }

      if (job.status === 'failed' && completedZipJobRef.current !== job.id) {
        completedZipJobRef.current = job.id
        getError(job.error_message || 'No se pudo generar el ZIP de reportes por usuario.')
      }
    },
    [getError, getSuccess]
  )

  const fetchZipJobStatus = useCallback(
    async (jobId: number) => {
      try {
        const response = await getZipReportJobStatus(jobId)
        const job = response?.data || response

        if (!job?.id) {
          throw new Error('No se pudo consultar el estado del ZIP.')
        }

        handleZipJobUpdate(job)

        if (job.status === 'completed' || job.status === 'failed') {
          localStorage.removeItem(ZIP_REPORT_JOB_STORAGE_KEY)
        }

        return job
      } catch (error) {
        logZipJobError('Error consultando estado del ZIP', error)
        throw error
      }
    },
    [handleZipJobUpdate]
  )

  useEffect(() => {
    const storedJobId = localStorage.getItem(ZIP_REPORT_JOB_STORAGE_KEY)

    if (!storedJobId || zipJobStatus?.id) {
      return
    }

    fetchZipJobStatus(Number(storedJobId)).catch(() => {
      localStorage.removeItem(ZIP_REPORT_JOB_STORAGE_KEY)
    })
  }, [fetchZipJobStatus, zipJobStatus?.id])

  useEffect(() => {
    if (!zipJobStatus || (zipJobStatus.status !== 'pending' && zipJobStatus.status !== 'processing')) {
      return
    }

    const intervalId = window.setInterval(() => {
      fetchZipJobStatus(zipJobStatus.id).catch((error) => {
        console.error(error)
      })
    }, ZIP_REPORT_POLLING_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [fetchZipJobStatus, zipJobStatus])

  const handleDownloadSelectedProcessesZip = async () => {
    if (processIds.size < 1) {
      return
    }

    if (processIds.size > 30) {
      getWarning('El limite maximo para descargar como ZIP es 30 procesos')
      return
    }

    try {
      setIsCreatingZipJob(true)
      const selectedProcessIds = Array.from(processIds)
      const response = await createZipReportJob(selectedProcessIds)
      const job = response?.data || response

      if (!job?.id) {
        getError('No se pudo iniciar la generacion del ZIP de reportes por usuario.')
        return
      }

      localStorage.setItem(ZIP_REPORT_JOB_STORAGE_KEY, String(job.id))
      completedZipJobRef.current = null
      setZipJobStatus(job)
      getSuccess('Empezamos a preparar el ZIP de reportes por usuario. Puedes seguir usando la plataforma.')
    } catch (error) {
      logZipJobError('Error creando job ZIP', error)
      getError('No se pudo iniciar la generacion del ZIP de reportes por usuario.')
    } finally {
      setIsCreatingZipJob(false)
    }
  }

  const handleDownloadReports = ({
    processId,
    processName,
    useBetesa,
    useCompetence,
    use360,
    isForAllProcess,
    initDate,
    endDate,
  }: DownloadProcess) => {
    setLoading(true)

    fetchGetReportsByExcel({
      processId: processId ?? 0,
      useBetesa,
      useCompetence,
      use360,
      processName: processName ?? '',
      isForAllProcess,
      initDate: initDate ?? '',
      endDate: endDate ?? '',
      processIds: Array.from(processIds) ?? [],
    }).then(() => setLoading(false))
  }

  const handleDownloadReportsAllProcesses = () => {
    handleDownloadReports({
      useBetesa: false,
      useCompetence: false,
      use360: false,
      isForAllProcess: true,
    })
  }

  const handleAddUsers = useCallback(
    (processId: number) => {
      getProcessDetail(processId)
        .then((response: any) => {
          setLoading(false)
          handleFinishProcess(response.process)
          navigate(platformAdminRoutes.CreateUsers, { state: { id: processId, isSendedProcess: true } })
        })
        .catch(() => {
          getError('Ha ocurrido un error al cargar este proceso')
          setLoading(false)
        })
    },
    [navigate, getError, handleFinishProcess]
  )

  const handlePagination = useCallback(
    (page: number) => {
      if (processSelected) return
      fetchProcesses({ sent: true, page, evaluatedOnly: false })
      setCurrentPage(page)
    },
    [fetchProcesses, setCurrentPage, processSelected]
  )

  const formattedProcesses = listProcess.map((process: any) => ({
    ...process,
    unanswered_ratio: getUnansweredRatio(process.unanswered_users || 0, process.count_users || 0),
  }))

  return (
    <MainLayout>
      <Container>
        <HeaderWrapper>
          <h2>Procesos creados</h2>
          <HeaderActions>
            <StrokedButton
              onClick={handleDownloadSelectedProcessesZip}
              type="button"
              disabled={processIds.size < 1 || isCreatingZipJob}
            >
              {isCreatingZipJob ? 'Preparando ZIP...' : 'Descargar reportes en ZIP'}
            </StrokedButton>
            <StrokedButton onClick={handleDateFilterButtonClick} type="button">
              Descargar resumen por rango de fechas
            </StrokedButton>
          </HeaderActions>
        </HeaderWrapper>
        {zipJobStatus ? (
          <JobStatusCard>
            <JobStatusHeader>
              <div>
                <JobStatusTitle>ZIP de reportes por usuario</JobStatusTitle>
                <JobStatusMeta>
                  Estado: {isPackagingZip ? 'empaquetando' : zipJobStatus.status} | Procesados {processedReports}/{totalReports} reportes
                  {zipJobStatus.failed_reports > 0 ? ` | Errores: ${zipJobStatus.failed_reports}` : ''}
                </JobStatusMeta>
              </div>
              {(zipJobStatus.status === 'completed' || zipJobStatus.status === 'failed') && (
                <StrokedButton onClick={clearZipJobTracking} type="button">
                  Cerrar
                </StrokedButton>
              )}
            </JobStatusHeader>
            <ProgressTrack>
              <ProgressFill $percent={zipJobStatus.progress_percent || 0} />
            </ProgressTrack>
          </JobStatusCard>
        ) : null}
        {!loadingListProcesses && !loading ? (
          <>
            {listProcess?.length > 0 ? (
              <>
                <Wrapper>
                  <ViewWrapper>
                    <SubtitleBold>Visualizar:</SubtitleBold>
                    <SelectSearch
                      showCheckboxes={false}
                      noChip={true}
                      label="Todos los procesos"
                      options={allProcess || []}
                      value={processSelected}
                      onChange={(e: any) => {
                        handleSelectProcess(e as number)
                      }}
                      limitTags={1}
                    />
                    <HighlightOffIcon
                      style={{ cursor: 'pointer', color: 'gray' }}
                      onClick={() => {
                        setProcessSelected('')
                      }}
                    />
                  </ViewWrapper>
                  <DownloadWrapper onClick={handleDownloadReportsAllProcesses}>
                    <DownloadIcon />
                    <TextBoldBlue style={{ fontSize: '1rem' }}>
                      {isDisabled
                        ? 'Descargar resumen de los procesos seleccionados'
                        : 'Descargar resumen de todos los procesos'}
                    </TextBoldBlue>
                  </DownloadWrapper>
                </Wrapper>
                <TableContainer>
                  <BluesiteTable
                    data={formattedProcesses}
                    headers={HEADERS_SENT_PROCESSES}
                    searchBar={false}
                    searchLabel=""
                    ActionComponent={ActionViewDownloadAddUsers}
                    totalPages={paginationListProcess.totalPages}
                    currentPage={currentPage}
                    handlePagination={handlePagination}
                    checks={true}
                    showNumbers={true}
                    totalItems={paginationListProcess.totalResults}
                    selectedItems={processIds}
                    setSelectedItems={(e) => setProcessIds(e)}
                    extraDataAction={{
                      onAddUsers: (e: number) => handleAddUsers(e),
                      onViewProcess: (e: number) => handleViewProcess(e),
                      onDownloadProcess: (e: IDownloadProcess) =>
                        handleDownloadReports({
                          processId: e.id,
                          processName: e.name,
                          useBetesa: e.betesaTest,
                          useCompetence: e.competencesTest,
                          use360: e.test_360,
                        }),
                      isDisabled,
                    }}
                  />
                </TableContainer>
              </>
            ) : (
              <WrapperEmptyState>
                <EmptyStateText>Esta compania no tiene procesos creados</EmptyStateText>
              </WrapperEmptyState>
            )}
          </>
        ) : (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        <ModalDateFilter
          showConfirmModal={showModalDateFilter}
          setShowModal={setShowModalDateFilter}
          downloadFunction={(e: any) => {
            handleDownloadReports({
              ...e,
              useBetesa: false,
              useCompetence: false,
              use360: false,
              isForAllProcess: true,
            })
          }}
        />
      </Container>
    </MainLayout>
  )
}

export default DeliveredProcesses
